import { api } from './api.js';
import { LIMITS, isSenderSignature, type SignatureAnchor } from '@worki/shared';
import { senderSignatureOf } from './anchors.js';
import { standardFontDataUrl } from './pdfFonts.js';

export interface DetectionText {
  str: string; width: number; height: number; transform: number[]; dir?: string;
}
export interface DetectionViewport {
  width: number; height: number;
  convertToViewportPoint: (x: number, y: number) => number[];
}
interface LabelBox { text: string; left: number; right: number; top: number; bottom: number; rtl: boolean }
const clean = (text: string) => text.normalize('NFKC').replace(/[\u0591-\u05c7\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
const senderLabel = /^(?:חתימת\s+(?:השולח|בעל\s+העסק|העסק)|(?:sender|business)\s+signature|signature\s+of\s+(?:the\s+)?(?:sender|business))$/;
const clientLabel = /לקוח|מקבל|נמען|חותם|\b(?:client|customer|recipient)\b/;
function explicitSender(text: string): boolean {
  const label = clean(text).replace(/^[\s:._()\-]+|[\s:._()\-]+$/g, '');
  const reversed = [...label].reverse().join('');
  return !clientLabel.test(label) && !clientLabel.test(reversed) && (senderLabel.test(label) || senderLabel.test(reversed));
}

/** PDF points become the displayed, rotated CropBox coordinate system before normalization. */
function textBox(item: DetectionText, viewport: DetectionViewport): LabelBox | null {
  if (item.transform.length !== 6) return null;
  const [a, b, c, d, x, y] = item.transform as [number, number, number, number, number, number];
  if (![a, b, c, d, x, y, item.width, item.height, viewport.width, viewport.height].every(Number.isFinite) || item.width <= 0 || item.height <= 0 || viewport.width <= 0 || viewport.height <= 0) return null;
  const along = Math.hypot(a, b), up = Math.hypot(c, d);
  if (!along || !up) return null;
  const start = viewport.convertToViewportPoint(x, y);
  const end = viewport.convertToViewportPoint(x + a / along * item.width, y + b / along * item.width);
  // The composer draws axis-aligned boxes. Vertical or skewed text is not a safe placement hint.
  if (Math.abs(end[1]! - start[1]!) > Math.abs(end[0]! - start[0]!) * 0.15) return null;
  const corners = [start, end,
    viewport.convertToViewportPoint(x + c / up * item.height, y + d / up * item.height),
    viewport.convertToViewportPoint(x + a / along * item.width + c / up * item.height, y + b / along * item.width + d / up * item.height)];
  return { text: item.str, left: Math.min(...corners.map(point => point[0]!)), right: Math.max(...corners.map(point => point[0]!)), top: Math.min(...corners.map(point => point[1]!)), bottom: Math.max(...corners.map(point => point[1]!)), rtl: item.dir === 'rtl' };
}

/** Text extraction only: scanned pages without readable text produce an empty result. */
export function detectSenderAnchors(items: DetectionText[], viewport: DetectionViewport, page: number): SignatureAnchor[] {
  const boxes = items.filter(item => item.str.trim()).map(item => textBox(item, viewport)).filter((box): box is LabelBox => !!box).sort((a, b) => a.top - b.top || a.left - b.left);
  const lines: LabelBox[][] = [];
  for (const box of boxes) {
    const line = lines.find(current => Math.abs(current[0]!.bottom - box.bottom) <= Math.max(box.bottom - box.top, current[0]!.bottom - current[0]!.top) * 0.4);
    if (line) line.push(box); else lines.push([box]);
  }
  const labels: LabelBox[] = [];
  for (const line of lines) {
    line.sort((a, b) => a.left - b.left);
    const clusters: LabelBox[][] = [];
    for (const box of line) {
      const cluster = clusters.at(-1), previous = cluster?.at(-1);
      if (previous && box.left - previous.right <= Math.max(box.bottom - box.top, previous.bottom - previous.top) * 2) cluster!.push(box);
      else clusters.push([box]);
    }
    for (const cluster of clusters) {
      const order = cluster.some(box => box.rtl) ? [...cluster].reverse() : cluster;
      const text = order.map(box => box.text).join(' ');
      if (!explicitSender(text)) continue;
      labels.push({ text, left: Math.min(...cluster.map(box => box.left)), right: Math.max(...cluster.map(box => box.right)), top: Math.min(...cluster.map(box => box.top)), bottom: Math.max(...cluster.map(box => box.bottom)), rtl: order[0]!.rtl });
    }
  }
  const anchors: SignatureAnchor[] = labels.flatMap(label => {
    const width = Math.min(0.3, Math.max(0.15, (label.right - label.left) / viewport.width + 0.04));
    const y = label.bottom / viewport.height + 0.01;
    const height = Math.min(0.07, 1 - y);
    if (y < 0 || height < 0.01) return [];
    return [{ page, kind: 'signature' as const, filledBy: 'sender' as const,
      x: Math.max(0, Math.min(1 - width, ((label.left + label.right) / 2) / viewport.width - width / 2)),
      y, width, height }];
  });
  return mergeSenderAnchors([], anchors).anchors;
}

/** A detected region never changes or replaces a manually placed customer/sender box. */
export function mergeSenderAnchors(existing: SignatureAnchor[], detected: SignatureAnchor[], image = senderSignatureOf(existing)): { anchors: SignatureAnchor[]; added: number } {
  const anchors = [...existing];
  for (const candidate of detected) {
    if (anchors.length >= LIMITS.maxSignatureAnchors) break;
    const overlaps = anchors.some(anchor => {
      if (anchor.page !== candidate.page) return false;
      const width = Math.min(anchor.x + anchor.width, candidate.x + candidate.width) - Math.max(anchor.x, candidate.x);
      const height = Math.min(anchor.y + anchor.height, candidate.y + candidate.height) - Math.max(anchor.y, candidate.y);
      const intersection = Math.max(0, width) * Math.max(0, height);
      return intersection > Math.min(anchor.width * anchor.height, candidate.width * candidate.height) * (isSenderSignature(anchor) ? 0.25 : 0.1);
    });
    if (!overlaps) anchors.push({ ...candidate, ...(image ? { image } : {}) });
  }
  return { anchors, added: anchors.length - existing.length };
}

export async function scanSenderAnchors(file: Blob): Promise<SignatureAnchor[]> {
  const data = new Uint8Array(await file.arrayBuffer());
  const [{ getDocument, PDFWorker }, { default: PdfWorker }] = await Promise.all([import('pdfjs-dist/legacy/build/pdf.mjs'), import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker')]);
  // An independent worker keeps scan cleanup from interrupting the live preview.
  const port = new PdfWorker();
  const worker = PDFWorker.create({ port });
  const loading = getDocument({ data, standardFontDataUrl, worker });
  const anchors: SignatureAnchor[] = [];
  try {
    const pdf = await loading.promise;
    for (let number = 1; number <= pdf.numPages; number++) {
      const page = await pdf.getPage(number);
      try {
        const text = await page.getTextContent();
        anchors.push(...detectSenderAnchors(text.items.filter((item): item is typeof item & DetectionText => 'str' in item), page.getViewport({ scale: 1 }), number));
      } finally { page.cleanup(); }
    }
    if(anchors.length) return anchors;
    const form=new FormData();form.append('file',file,'sender-scan.pdf');
    return (await api.upload<{anchors:SignatureAnchor[]}>('/signatures/detect-sender',form)).anchors;
  } finally {
    try { await loading.destroy(); }
    finally { worker.destroy(); port.terminate(); }
  }
}
